import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { usePandadocSync, usePandaDocDocuments } from 'core/hooks';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from '../StudentDetail.styled';
import { IStudent } from '../parts';

interface IPandaDocTabProps {
  student: IStudent;
  onChanged?: () => void;
}

/**
 * PandaDoc documents linked to this student through `agreement.student_id`.
 * Server-side filter (backend §1 — `pandaDocDocuments(studentId)`) so the
 * tab also surfaces documents in transit (signed but not yet provisioned)
 * and documents that ended in a different outcome (LOST/ACTIVE). The
 * client-side per-agreement collection that v1 used is gone — it missed
 * everything without a `pandadocDocument` link on `Agreement`.
 *
 * The «Sync PandaDoc» button kicks the global resync — useful when a doc
 * was signed but hasn't shown up yet. After a sync completes the hook
 * refetches the connection, so new rows appear automatically.
 */
export const PandaDocTab = memo(({ student, onChanged }: IPandaDocTabProps) => {
  const { t } = useTranslation();
  const { data, loading, totalCount, operations } = usePandaDocDocuments(
    { studentId: student.id },
    50,
  );
  const { triggerSync, syncing } = usePandadocSync(() => {
    operations.refetch();
    if (onChanged) onChanged();
  });

  return (
    <div style={{ marginTop: 16 }}>
      <S.HeaderRow>
        <S.SubTitle>
          {t('pages.studentDetail.pandadoc.hint', { count: totalCount })}
        </S.SubTitle>
        <L.HeaderActions>
          <L.PrimaryActionButton
            type="button"
            onClick={() => triggerSync()}
            disabled={syncing}
          >
            {syncing
              ? t('pages.studentDetail.pandadoc.syncing')
              : t('pages.studentDetail.pandadoc.syncNow')}
          </L.PrimaryActionButton>
        </L.HeaderActions>
      </S.HeaderRow>

      {data.length === 0 ? (
        <S.EmptyState>
          {loading
            ? t('pages.studentDetail.pandadoc.loading')
            : t('pages.studentDetail.pandadoc.noDocs')}
        </S.EmptyState>
      ) : (
        <S.DimWhenFetching fetching={loading}>
          <S.Card>
            <L.SmallTable>
              <thead>
                <tr>
                  <th>{t('pages.studentDetail.pandadoc.name')}</th>
                  <th>{t('pages.studentDetail.pandadoc.agreementCol')}</th>
                  <th>{t('pages.studentDetail.pandadoc.outcomeCol')}</th>
                  <th>{t('pages.studentDetail.pandadoc.signedAt')}</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {data.map((d) => {
                  const url = `https://app.pandadoc.com/a/#/documents/${d.pandadocId}`;
                  return (
                    <tr key={d.id}>
                      <td style={{ fontWeight: 600 }}>{d.name}</td>
                      <td>
                        {d.agreement?.agreementTitle ||
                          (d.provisionedAt
                            ? t('pages.studentDetail.pandadoc.linked')
                            : t('pages.studentDetail.pandadoc.notLinked'))}
                      </td>
                      <td>
                        {d.outcome === 'SIGNED' ? (
                          <L.Tag tone="ok">
                            {t('pages.studentDetail.pandadoc.outcomeSigned')}
                          </L.Tag>
                        ) : d.outcome === 'LOST' ? (
                          <L.Tag tone="danger">
                            {t('pages.studentDetail.pandadoc.outcomeLost')}
                          </L.Tag>
                        ) : (
                          <L.Tag tone="neutral">{d.outcome}</L.Tag>
                        )}
                      </td>
                      <td>
                        {d.dateCompleted
                          ? moment(d.dateCompleted).format('DD.MM.YYYY')
                          : '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <L.PrimaryActionButton
                          type="button"
                          onClick={() => window.open(url, '_blank')}
                        >
                          {t('pages.studentDetail.pandadoc.openInPandadoc')}
                          &nbsp;↗
                        </L.PrimaryActionButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </L.SmallTable>
          </S.Card>
        </S.DimWhenFetching>
      )}
    </div>
  );
});
